import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import SettingsToolbar from '../../components/settings/SettingsToolbar';
import TimeInput from '../../components/forms/TimeInput';
import * as api from './api';
import '../../css/db-backups.css';

// Database backups: the schedule, the manual trigger, and the log of every run
// (when it started and finished, how long it took, file size, rows per table,
// and the log text when something went wrong). Times are shown in Warsaw time,
// which is the zone the backend schedules in.

const ZONE = 'Europe/Warsaw';
const WEEKDAYS = [
   { id: 1, label: 'Пн' }, { id: 2, label: 'Вт' }, { id: 3, label: 'Ср' }, { id: 4, label: 'Чт' },
   { id: 5, label: 'Пт' }, { id: 6, label: 'Сб' }, { id: 7, label: 'Нд' },
];
const STATUS_LABEL = { RUNNING: 'Виконується', SUCCESS: 'Успішно', FAILED: 'Помилка' };
const POLL_RUNNING_MS = 4000;
const POLL_IDLE_MS = 30000;

const pad2 = (n) => String(n).padStart(2, '0');

function toDraft(config) {
   return {
      enabled: !!config.enabled,
      time: pad2(config.runHour) + ':' + pad2(config.runMinute),
      weekdays: [...(config.weekdays || [])].sort((a, b) => a - b),
      retentionCount: config.retentionCount,
   };
}

function fromDraft(draft) {
   const [hour, minute] = draft.time.split(':').map(Number);
   return {
      enabled: draft.enabled,
      runHour: hour,
      runMinute: minute,
      weekdays: draft.weekdays,
      retentionCount: Number(draft.retentionCount),
   };
}

// Returns the first problem with the draft, or '' when it can be saved.
function draftProblem(draft) {
   if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.time || '')) return 'Вкажіть час у форматі HH:mm.';
   if (draft.enabled && draft.weekdays.length === 0) return 'Оберіть принаймні один день тижня.';
   const keep = Number(draft.retentionCount);
   if (!Number.isInteger(keep) || keep < 1 || keep > 365) return 'Кількість копій: від 1 до 365.';
   return '';
}

function formatDateTime(iso) {
   if (!iso) return '—';
   const dt = DateTime.fromISO(String(iso)).setZone(ZONE);
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm:ss') : String(iso);
}

function formatBytes(bytes) {
   if (bytes == null) return '—';
   if (bytes < 1024) return bytes + ' B';
   if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
   return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

function formatDuration(seconds) {
   if (seconds == null) return '—';
   const m = Math.floor(seconds / 60);
   const s = seconds % 60;
   return m > 0 ? m + ' хв ' + s + ' с' : s + ' с';
}

function formatNumber(n) {
   if (n == null) return '—';
   return Number(n).toLocaleString('uk-UA');
}

export default function DbBackups() {
   const navigate = useNavigate();
   const [status, setStatus] = useState({ running: false, currentRunId: null, nextRunAt: null });
   const [config, setConfig] = useState(null);
   const [draft, setDraft] = useState(null);
   const [runs, setRuns] = useState([]);
   const [runsStatus, setRunsStatus] = useState('loading');
   const [selectedId, setSelectedId] = useState(null);
   const [detail, setDetail] = useState(null);
   const [busy, setBusy] = useState(false);
   const [saving, setSaving] = useState(false);
   const [notice, setNotice] = useState('');
   const [error, setError] = useState('');

   const handleError = useCallback((err) => {
      if (err && err.response && err.response.status === 401) navigate('/login');
      return err && err.response && typeof err.response.data === 'string' ? err.response.data : '';
   }, [navigate]);

   const loadStatus = useCallback(() => {
      api.getBackupStatus()
         .then((res) => setStatus(res.data))
         .catch((err) => handleError(err));
   }, [handleError]);

   const loadRuns = useCallback(() => {
      api.getBackupRuns(50)
         .then((res) => {
            setRuns(Array.isArray(res.data) ? res.data : []);
            setRunsStatus('ready');
         })
         .catch((err) => { handleError(err); setRunsStatus('error'); });
   }, [handleError]);

   useEffect(() => {
      api.getBackupConfig()
         .then((res) => {
            setConfig(res.data);
            setDraft(toDraft(res.data));
         })
         .catch((err) => handleError(err));
      loadStatus();
      loadRuns();
   }, [loadStatus, loadRuns, handleError]);

   // Refresh faster while a run is in progress, so the row turns to its result on its own.
   useEffect(() => {
      const tick = () => { loadStatus(); loadRuns(); };
      const handle = setInterval(tick, status.running ? POLL_RUNNING_MS : POLL_IDLE_MS);
      return () => clearInterval(handle);
   }, [status.running, loadStatus, loadRuns]);

   // Details of the selected run; refreshed with the list so a running run's log keeps growing.
   useEffect(() => {
      if (selectedId == null) { setDetail(null); return; }
      api.getBackupRun(selectedId)
         .then((res) => setDetail(res.data))
         .catch((err) => handleError(err));
   }, [selectedId, runs, handleError]);

   const problem = useMemo(() => (draft ? draftProblem(draft) : ''), [draft]);
   const dirty = useMemo(() => {
      if (!config || !draft) return false;
      return JSON.stringify(fromDraft(draft)) !== JSON.stringify({
         enabled: !!config.enabled,
         runHour: config.runHour,
         runMinute: config.runMinute,
         weekdays: [...(config.weekdays || [])].sort((a, b) => a - b),
         retentionCount: config.retentionCount,
      });
   }, [config, draft]);

   const toggleWeekday = (id) => {
      setDraft((prev) => ({
         ...prev,
         weekdays: prev.weekdays.includes(id)
            ? prev.weekdays.filter((d) => d !== id)
            : [...prev.weekdays, id].sort((a, b) => a - b),
      }));
   };

   const saveConfig = () => {
      if (saving || problem || !dirty) return;
      setSaving(true);
      setError('');
      setNotice('');
      api.saveBackupConfig(fromDraft(draft))
         .then((res) => {
            setConfig(res.data);
            setDraft(toDraft(res.data));
            setNotice('Розклад збережено.');
            loadStatus();
         })
         .catch((err) => setError(handleError(err) || 'Не вдалося зберегти розклад.'))
         .finally(() => setSaving(false));
   };

   const runNow = () => {
      if (busy || status.running) return;
      setBusy(true);
      setError('');
      setNotice('');
      api.startBackupRun()
         .then((res) => {
            setSelectedId(res.data.id);
            setNotice('Копію запущено.');
            loadStatus();
            loadRuns();
         })
         .catch((err) => {
            if (err.response && err.response.status === 409) {
               setError('Копія вже триває. Дочекайтеся її завершення.');
            } else {
               setError(handleError(err) || 'Не вдалося запустити копію.');
            }
            loadStatus();
         })
         .finally(() => setBusy(false));
   };

   const removeRun = (run) => {
      if (run.status === 'RUNNING') return;
      if (!window.confirm('Видалити копію ' + run.fileName + '? Файл буде видалено з диска.')) return;
      setBusy(true);
      setError('');
      api.deleteBackupRun(run.id)
         .then(() => {
            if (selectedId === run.id) setSelectedId(null);
            setNotice('Копію видалено.');
            loadRuns();
         })
         .catch((err) => setError(handleError(err) || 'Не вдалося видалити копію.'))
         .finally(() => setBusy(false));
   };

   const maxRows = detail && detail.tables.length ? Math.max(...detail.tables.map((t) => t.rowCount)) : 0;

   return (
      <Layout>
         <SettingsToolbar />
         <PageHeader name='Резервні копії БД' />
         <main className='main-block'>
            <div className='main-content db-bk-page'>

               <section className='db-bk-status'>
                  <div className='db-bk-status-main'>
                     <span className={'db-bk-status-dot' + (status.running ? ' is-running' : '')} />
                     <span className='db-bk-status-text'>
                        {status.running ? 'Триває створення копії…' : 'Активної копії немає'}
                     </span>
                     <span className='db-bk-muted'>
                        Наступна за розкладом: {status.nextRunAt ? formatDateTime(status.nextRunAt) : 'розклад вимкнено'}
                     </span>
                  </div>
                  <button type='button' className='db-bk-btn db-bk-btn-primary' disabled={busy || status.running} onClick={runNow}>
                     Зробити копію зараз
                  </button>
               </section>

               {notice ? <p className='db-bk-notice'>{notice}</p> : ''}
               {error ? <p className='db-bk-error'>{error}</p> : ''}

               <div className='db-bk-columns'>
               <div className='db-bk-col-schedule'>
               <section className='db-bk-card'>
                  <h3 className='db-bk-card-title'>Розклад</h3>
                  {draft ? (
                     <>
                        <label className='db-bk-switch'>
                           <input type='checkbox' checked={draft.enabled}
                              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                           <span className='db-bk-switch-track'><span className='db-bk-switch-thumb' /></span>
                           <span>Автоматичні копії увімкнені</span>
                        </label>

                        <div className='db-bk-form-row'>
                           <div className='db-bk-field'>
                              <span className='db-bk-label'>Година</span>
                              <TimeInput value={draft.time} onChange={(v) => setDraft({ ...draft, time: v })} />
                           </div>
                           <div className='db-bk-field'>
                              <span className='db-bk-label'>Зберігати останніх копій</span>
                              <input className='db-bk-input' type='number' min='1' max='365' value={draft.retentionCount}
                                 onChange={(e) => setDraft({ ...draft, retentionCount: e.target.value })} />
                           </div>
                        </div>

                        <div className='db-bk-field'>
                           <span className='db-bk-label'>Дні тижня</span>
                           <div className='db-bk-weekdays'>
                              {WEEKDAYS.map((d) => (
                                 <button type='button' key={d.id}
                                    className={'db-bk-day' + (draft.weekdays.includes(d.id) ? ' is-on' : '')}
                                    onClick={() => toggleWeekday(d.id)}>
                                    {d.label}
                                 </button>
                              ))}
                           </div>
                        </div>

                        <div className='db-bk-actions'>
                           {problem ? <span className='db-bk-muted db-bk-problem'>{problem}</span> : ''}
                           <button type='button' className='db-bk-btn db-bk-btn-primary' disabled={saving || !!problem || !dirty} onClick={saveConfig}>
                              {saving ? 'Збереження…' : 'Зберегти розклад'}
                           </button>
                        </div>
                     </>
                  ) : (
                     <p className='db-bk-muted'>Завантаження…</p>
                  )}
               </section>
               </div>

               <div className='db-bk-col-history'>
               <section className='db-bk-card'>
                  <h3 className='db-bk-card-title'>Історія копій</h3>
                  {runsStatus === 'error' ? <p className='db-bk-error'>Не вдалося завантажити історію копій.</p> : ''}
                  {runsStatus === 'ready' && runs.length === 0 ? <p className='db-bk-muted'>Копій ще не створювали.</p> : ''}
                  {runs.length > 0 ? (
                     <div className='db-bk-table-wrap'>
                        <table className='db-bk-table'>
                           <thead>
                              <tr>
                                 <th>Початок</th>
                                 <th>Тривалість</th>
                                 <th>Статус</th>
                                 <th>Файл</th>
                                 <th className='db-bk-num'>Розмір</th>
                                 <th className='db-bk-num'>Записи</th>
                                 <th />
                              </tr>
                           </thead>
                           <tbody>
                              {runs.map((run) => (
                                 <tr key={run.id} className={'db-bk-row' + (selectedId === run.id ? ' is-selected' : '') + ' is-' + run.status.toLowerCase()}
                                    onClick={() => setSelectedId((prev) => (prev === run.id ? null : run.id))}>
                                    <td>{formatDateTime(run.startedAt)}</td>
                                    <td>{formatDuration(run.durationSeconds)}</td>
                                    <td><span className={'db-bk-chip is-' + run.status.toLowerCase()}>{STATUS_LABEL[run.status] || run.status}</span></td>
                                    <td className='db-bk-file'>{run.fileName || '—'}</td>
                                    <td className='db-bk-num'>{formatBytes(run.fileSizeBytes)}</td>
                                    <td className='db-bk-num'>{formatNumber(run.totalRows)}</td>
                                    <td className='db-bk-row-actions' onClick={(e) => e.stopPropagation()}>
                                       {run.status === 'SUCCESS' ? (
                                          <a className='db-bk-link' href={api.backupRunFileUrl(run.id)} download>Завантажити</a>
                                       ) : ''}
                                       {run.status !== 'RUNNING' ? (
                                          <button type='button' className='db-bk-btn db-bk-btn-danger db-bk-btn-sm' disabled={busy} onClick={() => removeRun(run)}>Видалити</button>
                                       ) : ''}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>
                  ) : ''}
               </section>

               {detail ? (
                  <section className='db-bk-card'>
                     <div className='db-bk-card-title-row'>
                        <h3 className='db-bk-card-title'>Деталі копії</h3>
                        <button type='button' className='db-bk-close' title='Закрити деталі' aria-label='Закрити деталі'
                           onClick={() => setSelectedId(null)}>×</button>
                     </div>
                     <div className='db-bk-detail-head'>
                        <span className={'db-bk-chip is-' + detail.status.toLowerCase()}>{STATUS_LABEL[detail.status] || detail.status}</span>
                        <span className='db-bk-file'>{detail.fileName || 'немає файлу'}</span>
                        <span className='db-bk-muted'>
                           {formatDateTime(detail.startedAt)} → {detail.finishedAt ? formatDateTime(detail.finishedAt) : 'триває'}
                           {' · '}{formatDuration(detail.durationSeconds)}
                           {' · '}{formatBytes(detail.fileSizeBytes)}
                           {' · '}{formatNumber(detail.totalRows)} записів
                           {detail.tableCount ? ' · ' + detail.tableCount + ' таблиць' : ''}
                        </span>
                     </div>
                     {detail.errorMessage ? <p className='db-bk-error db-bk-error-box'>{detail.errorMessage}</p> : ''}

                     <div className='db-bk-detail-grid'>
                        <div>
                           <h4 className='db-bk-sub'>Записи в таблицях</h4>
                           {detail.tables.length === 0 ? <p className='db-bk-muted'>Даних про таблиці немає.</p> : (
                              <div className='db-bk-tables'>
                                 {detail.tables.map((t) => (
                                    <div key={t.tableName} className='db-bk-table-row'>
                                       <span className='db-bk-table-name'>{t.tableName}</span>
                                       <span className='db-bk-table-bar'>
                                          <i style={{ width: maxRows ? (t.rowCount / maxRows * 100) + '%' : '0%' }} />
                                       </span>
                                       <span className='db-bk-num'>{formatNumber(t.rowCount)}</span>
                                    </div>
                                 ))}
                              </div>
                           )}
                        </div>
                        <div>
                           <h4 className='db-bk-sub'>Лог</h4>
                           <pre className='db-bk-log'>{detail.log || 'Записів у лозі немає.'}</pre>
                        </div>
                     </div>
                  </section>
               ) : ''}
               </div>
               </div>
            </div>
         </main>
      </Layout>
   );
}
